import type { HomePageData, ImageRefDto } from '../../../shared/models/marketing.models';
import { resolveMilestoneCtaHref } from '../../utils/milestone-cta';
import { successStorySlug } from '../../utils/milestone-cta';

export type SuccessStoryItem = HomePageData['successStories']['items'][number];

export type StoryCardModel = {
  id: string;
  pill?: string;
  /** Display name (student or story headline). */
  name: string;
  /** University, country, or secondary line. */
  meta?: string;
  description: string;
  ctaHref?: string;
  ctaLabel?: string;
  image?: ImageRefDto;
  kind?: 'success-story' | 'testimonial';
  category?: string;
  university?: string;
  country?: string;
  course?: string;
  intake?: string;
  rating?: number;
};

export type StoryStat = { value: string; label: string };

export const DEFAULT_SUCCESS_STORIES_HERO_STATS: StoryStat[] = [
  { value: '500+', label: 'Global Placements' },
  { value: '98%', label: 'Visa Success' },
  { value: '$2M+', label: 'Scholarships Secured' },
];

export const STORY_CATEGORIES = [
  { id: 'all', label: 'All Stories' },
  { id: 'scholarships', label: 'Scholarships' },
  { id: 'masters', label: "Master's Programs" },
  { id: 'phd', label: 'PhD Research' },
  { id: 'journeys', label: 'Student Journeys' },
] as const;

export type StoryCategoryId = (typeof STORY_CATEGORIES)[number]['id'];

export const HOME_SLIDER_STORIES_LIMIT = 4;
export const LANDING_STORIES_LIMIT = 6;
export const STORIES_PAGE_SIZE = 6;

type WithOptionalCreatedAt = { createdAt?: string };

/** Newest first; items without `createdAt` treat later array position as newer. */
export function sortByCreatedAtDesc<T extends WithOptionalCreatedAt>(items: T[]): T[] {
  return items
    .map((item, index) => ({
      item,
      index,
      time: item.createdAt ? Date.parse(item.createdAt) : Number.NaN,
    }))
    .sort((a, b) => {
      const aValid = Number.isFinite(a.time);
      const bValid = Number.isFinite(b.time);
      if (aValid && bValid) return b.time - a.time;
      if (aValid) return -1;
      if (bValid) return 1;
      return b.index - a.index;
    })
    .map(({ item }) => item);
}

const CATEGORY_KEYWORDS: Record<Exclude<StoryCategoryId, 'all'>, string[]> = {
  scholarships: ['scholarship', 'award', 'grant', 'funding', 'excellence'],
  masters: ['master', 'msc', 'mba', 'm.sc', "master's", 'postgraduate'],
  phd: ['phd', 'research', 'doctorate', 'doctoral', 'phd track'],
  journeys: ['journey', 'student', 'experience', 'placement'],
};

export function storyCardFromSuccessStory(story: SuccessStoryItem): StoryCardModel {
  const lines = story.title
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const name = lines[0] ?? story.title.trim();
  const meta = lines.length > 1 ? lines.slice(1).join(' · ') : undefined;

  return {
    id: `story-${successStorySlug(story.title)}`,
    pill: story.pill?.trim() || undefined,
    name,
    meta,
    description: story.description,
    ctaHref: resolveMilestoneCtaHref(story.ctaHref, story.title),
    ctaLabel: story.ctaLabel?.trim() || undefined,
    image: story.image,
    kind: 'success-story',
    category: story.pill?.trim() || undefined,
  };
}

export function mapSuccessStoryCards(stories: SuccessStoryItem[]): StoryCardModel[] {
  return sortByCreatedAtDesc(stories).map(storyCardFromSuccessStory);
}

export function storySearchText(story: StoryCardModel): string {
  return [story.name, story.meta, story.description, story.pill].filter(Boolean).join(' ');
}

export function matchesCategory(story: StoryCardModel, categoryId: StoryCategoryId): boolean {
  if (categoryId === 'all') return true;
  const haystack = storySearchText(story).toLowerCase();
  return CATEGORY_KEYWORDS[categoryId].some((keyword) => haystack.includes(keyword));
}

export function matchesSearch(story: StoryCardModel, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return storySearchText(story).toLowerCase().includes(normalized);
}

export function heroStatsFromHome(stats?: StoryStat[]): StoryStat[] {
  if (stats && stats.length >= 3) return stats.slice(0, 3);
  return DEFAULT_SUCCESS_STORIES_HERO_STATS;
}

export function storyCardSlug(card: StoryCardModel): string {
  return successStorySlug(card.name);
}
